import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { Skeleton, Table, Tag, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { contractsStatuses } from '../../utils/constants/tagColors';
import { routes, subRoutes } from '../../utils/constants/routes';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import PageHeader from '../../components/PageHeader/PageHeader';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import DescriptionList from '../../components/DescriptionList/DescriptionList';
import useFields from './fields';

const { Column } = Table;
const iconSize = 18;

const Customer = () => {
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState();
  const [contracts, setContracts] = useState();
  const fields = useFields(t, customer);

  const getCustomer = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/customers/${id}?populate=internal_comments.author`
      });
      setCustomer(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getContracts = async () => {
    try {
      let url = `/contracts?customer.email=${customer.email}&populate=product,insurer`;
      if (user?.insurer) {
        url = `/contracts?customer.email=${customer.email}&insurer=${user?.insurer._id}&populate=product,insurer`;
      }
      const { data } = await dispatchAPI('GET', { url });
      setContracts(data.reverse());
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getCustomer();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      await getContracts();
      setIsLoading(false);
    })();
    // eslint-disable-next-line
  }, [customer]);

  return (
    <>
      <PageHeader
        title={customer && `${customer.first_name} ${customer.last_name}`}
      />
      <ContentCustom>
        <Skeleton loading={isLoading}>
          <DescriptionList data={fields} translate />
        </Skeleton>
      </ContentCustom>
      <PageHeader title={t('customers.contracts')} />
      <ContentCustom>
        {contracts && (
          <Table
            dataSource={contracts
              .filter(({ status }) => status !== 'QUOTE')
              .filter((amendment) => !amendment.previous_contract)}
            pagination={contracts.length > 10}
            scroll={{ x: 800 }}
          >
            <Column
              title={t('contracts.form.number')}
              dataIndex="number"
              sorter
              render={(number, data) => (
                <Link
                  to={`${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${data._id}`}
                >
                  {number}
                </Link>
              )}
            />
            <Column
              title={t('commissions.form.contract_status')}
              dataIndex="status"
              sorter
              render={(status) => (
                <Tag color={contractsStatuses[status]}>
                  {t(`contracts.tags.${status}`)}
                </Tag>
              )}
            />
            <Column
              title={t('contracts.form.effective_date')}
              dataIndex="effective_date"
              sorter
              render={(effective_date) =>
                moment(effective_date).format('DD/MM/YYYY')
              }
            />
            <Column
              title={t('contracts.form.product')}
              dataIndex="product"
              sorter
              render={(product) => product?.name}
            />
            <Column
              title={t('contracts.form.consulting_broker')}
              dataIndex="insurer"
              sorter
              render={(insurer) => insurer?.name}
            />
            <Column
              fixed="right"
              width="6%"
              render={(element) =>
                element && (
                  <>
                    <Tooltip placement="top" title="Ouvrir">
                      <Link
                        to={{
                          pathname:
                            element.status === 'QUOTE'
                              ? `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${element._id}`
                              : `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${element._id}`
                        }}
                      >
                        <EyeOutlined
                          style={{
                            fontSize: iconSize,
                            color: 'var(--textColor)'
                          }}
                        />
                      </Link>
                    </Tooltip>
                  </>
                )
              }
            />
          </Table>
        )}
      </ContentCustom>
      <PageHeader title={t('customers.quotes')} />
      <ContentCustom>
        {contracts && (
          <Table
            dataSource={contracts
              .filter((amendment) => !amendment.previous_contract)
              .filter(({ status }) => status === 'QUOTE')}
            pagination={contracts.length > 10}
            scroll={{ x: 800 }}
          >
            <Column
              title={t('contracts.form.number_quote')}
              dataIndex="number"
              sorter
              render={(number, data) => (
                <Link
                  to={`${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${data._id}`}
                >
                  {number}
                </Link>
              )}
            />
            <Column
              title={t('commissions.form.contract_status')}
              dataIndex="status"
              render={(status) => (
                <Tag color={contractsStatuses[status]}>
                  {t(`contracts.tags.${status}`)}
                </Tag>
              )}
            />
            <Column
              title={t('contracts.form.effective_date')}
              dataIndex="created_at"
              render={(created_at) => moment(created_at).format('DD/MM/YYYY')}
            />
            <Column
              title={t('contracts.form.product')}
              dataIndex="product"
              render={(product) => product?.name}
            />
            <Column
              title={t('contracts.form.consulting_broker')}
              dataIndex="insurer"
              render={(insurer) => insurer?.name}
            />
            <Column
              fixed="right"
              width="6%"
              render={(element) =>
                element && (
                  <>
                    <Tooltip placement="top" title="Ouvrir">
                      <Link
                        to={{
                          pathname:
                            element.status === 'QUOTE'
                              ? `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${element._id}`
                              : `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${element._id}`
                        }}
                      >
                        <EyeOutlined
                          style={{
                            fontSize: iconSize,
                            color: 'var(--textColor)'
                          }}
                        />
                      </Link>
                    </Tooltip>
                  </>
                )
              }
            />
          </Table>
        )}
      </ContentCustom>
    </>
  );
};

export default Customer;
